import React from "react"
import Layout from "../../../components/Layout"
import PageHero from "../../../components/PageHero"
import News from "../../../components/News"
import "../../../styles/news.scss"
import "../../../styles/caseStudy.scss"
import heroImg from "../../../images/caseStudies/orangelinetrain1.jpg"
import clientLogo from "../../../images/clients/Colored/orangeline_logo.png"

const CHALLENGES = [
  {
    title: "Fragmented Project Planning",
    desc: "Reliance on manual spreadsheets and disconnected documents led to inconsistencies in timelines and resource allocation.",
  },
  {
    title: "Approval Bottlenecks",
    desc: "Manual approval cycles for site work and procurement caused significant delays, slowing down the overall project momentum.",
  },
  {
    title: "Execution Blind Spots",
    desc: "A lack of real-time visibility into site activities made it difficult for leadership to monitor progress or identify issues as they occurred.",
  },
  {
    title: "Reporting Delays",
    desc: "Consolidating data from multiple sites into meaningful reports was time-consuming, often resulting in stale information that hindered strategic pivots.",
  },
]

const SOLUTIONS = [
  { num: "01", text: "Construction Planning Suite", desc: "A robust module for defining project milestones, task dependencies, and resource scheduling to ensure a clear roadmap for every build." },
  { num: "02", text: "Automated Approval Engine", desc: "Digital workflows for tracking and approving site requests, change orders, and procurement, reducing administrative lag significantly." },
  { num: "03", text: "Real-time Execution Monitoring", desc: "A mobile-enabled field solution that allows site managers to log daily progress, upload photos, and track labor productivity in real-time." },
  { num: "04", text: "Live Reporting Dashboards", desc: "Comprehensive business intelligence tools that provide leadership with an instant view of project health, budgets, and execution timelines." },
  { num: "05", text: "Document & Compliance Vault", desc: "A centralized repository for blueprints, permits, and inspection reports, ensuring all stakeholders have access to the latest project version." },
]

const RESULTS = [
  { metric: "30%", label: "Faster project turnaround from planning to final handover" },
]

const RESULT_BULLETS = [
  "Significant reduction in site errors due to real-time tracking and automated approval checkpoints",
  "Improved team alignment between the head office and field staff through synchronized task management",
  "Enhanced stakeholder trust with transparent, live reporting on project status and budget utilization",
  "Data-driven leadership equipped with actionable insights to optimize resource distribution across multiple active sites",
]

const OrangelinePakistan = () => (
  <div className="CaseStudeies">
    <Layout
      pageTitle="Visiomate - Orangeline Pakistan Case Study"
      descrip="Construction Management Solution for Orangeline Pakistan — planning, approvals, real-time monitoring, and live dashboards built by Visiomate."
    >
      <PageHero
        eyebrow="Customer Story"
        title="Orange Line Train Pakistan"
        description="Centralized construction management from planning to final handover"
      />

      {/* About */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner cs-intro">
          <div className="cs-intro__img">
            <img src={heroImg} alt="Orangeline Pakistan" />
          </div>
          <div className="cs-intro__text">
            <img src={clientLogo} alt="Orangeline Pakistan logo" className="cs-client-logo" />
            <h2 className="vm-news-heading vm-news-heading--dark">
              Construction Management Solution for Orangeline Pakistan
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Orangeline Pakistan is a forward-thinking construction firm dedicated to delivering
              high-quality infrastructure and building projects across the region. Committed to
              precision and operational excellence, they focus on modernizing traditional building
              practices through digital integration. By prioritizing data-driven decision-making, they
              ensure that every project is executed with maximum efficiency and transparency.
            </p>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What They Were Up Against</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Before working with Visiomate, Orangeline faced four critical operational gaps across
              their project lifecycle.
            </p>
          </div>
          <div className="cs-challenge-grid">
            {CHALLENGES.map(c => (
              <div key={c.title} className="cs-challenge-card">
                <div className="cs-challenge-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 className="cs-challenge-card__title">{c.title}</h3>
                <p className="cs-challenge-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What We Built</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              We designed and implemented a centralized Construction Management Solution to unify
              Orangeline's entire operational lifecycle.
            </p>
          </div>
          <div className="cs-solution-layout">
            <div className="cs-solutions-grid">
              {SOLUTIONS.map(s => (
                <div key={s.num} className="cs-solution-item">
                  <div className="cs-solution-item__badge">{s.num}</div>
                  <div>
                    <span className="cs-solution-item__text">{s.text}</span>
                    <p className="cs-solution-item__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="cs-solution-img">
              <img src={heroImg} alt="Orangeline Pakistan" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">A Dramatic Transformation</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Orangeline Pakistan saw measurable impact from planning through to site execution.
            </p>
          </div>
          <div className="cs-results-grid">
            {RESULTS.map(r => (
              <div key={r.metric} className="cs-result-card">
                <span className="cs-result-card__metric">{r.metric}</span>
                <p className="cs-result-card__label">{r.label}</p>
              </div>
            ))}
            {RESULT_BULLETS.map(b => (
              <div key={b} className="cs-result-card cs-result-card--bullet">
                <div className="cs-result-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="cs-result-card__label">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      {/* <section className="vm-news-section vm-news-section--dark">
        <div className="vm-news-inner cs-conclusion">
          <h2 className="vm-news-heading">The Outcome</h2>
          <p className="vm-news-sub">
            By unifying their entire construction workflow into one seamless digital system, Orangeline
            Pakistan achieved faster operations, greater transparency, and higher execution standards.
            What was once a fragmented, manual process is now a streamlined, data-driven workflow —
            positioning Orangeline Pakistan as a modern, technology-centric leader in the competitive
            construction industry.
          </p>
        </div>
      </section> */}

      <News isAbout={true} />
    </Layout>
  </div>
)

export default OrangelinePakistan
